import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import moment, { Moment } from 'moment'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ContractTooltip from 'modules/contracts/components/ContractDetails/ContractTooltip'

interface Props {
  startDate?: Moment | null
}

function calculateDifference(startDate: moment.Moment): {
  years: number
  months: number
  days: number
  isPast: boolean
} {
  const now = moment()
  const isPast = startDate.isBefore(now)
  // Determine the earlier and later dates
  const earlierDate = isPast ? startDate.clone() : now
  const laterDate = isPast ? now : startDate.clone()

  // Calculate the difference in years, months, and days
  const years = laterDate.diff(earlierDate, 'years')
  earlierDate.add(years, 'years') // Move the earlier date forward by the number of years

  const months = laterDate.diff(earlierDate, 'months')
  earlierDate.add(months, 'months') // Move the earlier date forward by the number of months

  const days = laterDate.diff(earlierDate, 'days')

  // Build the result object
  const result = {
    years,
    months,
    days,
    isPast,
  }

  return result
}

const StartDate = ({ startDate }: Props) => {
  const { years, months, days, isPast } = startDate
    ? calculateDifference(startDate)
    : { years: 0, months: 0, days: 0, isPast: false }
  const formattedStartDate = startDate ? dateToString(startDate, 'ui_dmy_long') : null
  const tooltipInformation = isPast
    ? i18n.t('contract_versions.contract_information.start_date_tooltip_past', {
        startDate: formattedStartDate,
      })
    : i18n.t('contract_versions.contract_information.start_date_tooltip_future', {
        startDate: formattedStartDate,
      })
  const tenureYears =
    years > 0
      ? i18n.tp('contract_versions.contract_information.start_date_years', { count: years })
      : ''
  const tenureMonths =
    months > 0
      ? i18n.tp('contract_versions.contract_information.start_date_months', { count: months })
      : ''
  const tenureDays =
    days > 0
      ? i18n.tp('contract_versions.contract_information.start_date_days', { count: days })
      : ''
  const tenure = `${tenureYears} ${tenureMonths} ${tenureDays}`.trim()
  return (
    <Box gridColSpan={{ xxs: 12, xs: 6, s: 3 }} gap="s2">
      <Box gap="s8">
        <Box flexDirection="row" alignItems="center" gap="s4" height="s24">
          <Text size="200" weight="normal" color="grey800">
            {i18n.t('contract_versions.contract_information.start_date')}
          </Text>
          {startDate && (
            <ContractTooltip information={tooltipInformation} icon="info-circle-line" />
          )}
        </Box>
        {startDate && (
          <Text size="300" weight="medium" color="grey1000">
            {formattedStartDate}
          </Text>
        )}
        {!startDate && (
          <Text size="300" weight="medium" color="grey600">
            {i18n.t('contract_versions.contract_information.not_informed')}
          </Text>
        )}
      </Box>
      <Text size="200" weight="normal" color="grey800">
        {tenure}
      </Text>
    </Box>
  )
}

export default observer(StartDate)
